import { RouteNames } from '@/models/enums/RouteNames';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import type { RouteRecordRaw } from 'vue-router';
import { useJobArchitectureStore } from './jobArchitectureStore';

export const jobArchitectureRoutes: RouteRecordRaw[] = [
    {
        path: 'overview',
        name: RouteNames.AdministratorJobArchitectureOverview,
        component: async () => import('@/views/jobArchitecture/overview/Overview.vue'),
        meta: {
            requiresAuth: true,
            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
        },
    },
    {
        path: 'employments',
        component: async () => import('@/views/jobArchitecture/manageEmployments/templates/TemplateManageEmployments.vue'),
        name: RouteNames.AdministratorJobArchitectureManageEmploymentsV2,
        redirect: { name: RouteNames.AdministratorJobArchitectureEmployeeCopilotOverviewV2 },
        meta: {
            requiresAuth: true,
            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
        },
        children: [
            {
                path: 'copilot',
                children: [
                    {
                        path: '',
                        name: RouteNames.AdministratorJobArchitectureEmployeeCopilotOverviewV2,
                        component: async () => import('@/views/jobArchitecture/manageEmployments/copilot/Overview.vue'),
                        meta: {
                            requiresAuth: true,
                            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                        },
                    },
                    {
                        path: 'newly-hired',
                        name: RouteNames.AdministratorJobArchitectureEmployeeCopilotNewlyHiredSuggestionsV2,
                        component: async () => import('@/views/jobArchitecture/manageEmployments/copilot/newEmployments/EmployeeCopilotNewHires.vue'),
                        meta: {
                            requiresAuth: true,
                            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                        },
                    },
                    {
                        path: 'updated-employments/:id',
                        name: RouteNames.AdministratorJobArchitectureEmployeeCopilotSuggestionV2,
                        component: async () => import('@/views/jobArchitecture/manageEmployments/copilot/updatedEmployments/EmployeeCopilotSuggestions.vue'),
                        props: router => ({
                            id: router.params.id,
                        }),
                        meta: {
                            requiresAuth: true,
                            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                        },
                    },
                ]
            },
            {
                path: 'unassigned',
                name: RouteNames.AdministratorJobArchitectureManageEmploymentsUnassigned,
                component: async () => import('@/views/jobArchitecture/manageEmployments/employments/ManageUnassignedEmploymentsDraft.vue'),
                meta: {
                    requiresAuth: true,
                    userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                },
            },
            {
                path: 'all',
                name: RouteNames.AdministratorJobArchitectureManageEmploymentsAll,
                component: async () => import('@/views/jobArchitecture/manageEmployments/employments/ManageAllEmploymentsDraft.vue'),
                meta: {
                    requiresAuth: true,
                    userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
                },
            }
        ]
    },
    {
        path: 'import',
        name: RouteNames.AdministratorJobArchitectureImport,
        component: async () => import('@/views/jobArchitecture/import/Import.vue'),
        meta: {
            requiresAuth: true,
            userTypes: [Role.Root, Role.SystemAdministrator]
        },
    },
    {
        path: 'jobs/:id?',
        name: RouteNames.AdministratorJobArchitectureManageJobs,
        component: async () => import('@/views/jobArchitecture/manageJobProfiles/ManageJobs.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
        },
    },
    {
        path: 'evaluation/:jobProfile?',
        name: RouteNames.AdministratorJobArchitectureEvaluation,
        component: async () => import('@/views/jobArchitecture/evaluation/FactorEvaluation.vue'),
        props: true,
        meta: {
            requiresAuth: true,
            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
        },
    },
    {
        path: 'pay-ranges-v2',
        name: RouteNames.AdministratorJobArchitecturePayRangesV2,
        component: async () => import('@/views/jobArchitecture/payRangesV2/PayRanges.vue'),
        beforeEnter: (): { name: string } | undefined => {
            if (!useJobArchitectureStore().isDraftMode) {
                return { name: RouteNames.AdministratorJobArchitecturePayRangesPublishedV2 };
            }
            return undefined;
        },
        meta: {
            requiresAuth: true,
            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
        },
    },
    {
        path: 'pay-ranges-v2-published',
        name: RouteNames.AdministratorJobArchitecturePayRangesPublishedV2,
        component: async () => import('@/views/jobArchitecture/payRangesV2/PayRangesPublished.vue'),
        beforeEnter: (): { name: string } | undefined => {
            if (useJobArchitectureStore().isDraftMode) {
                return { name: RouteNames.AdministratorJobArchitecturePayRangesV2 };
            }
            return undefined;
        },
        meta: {
            requiresAuth: true,
            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
        },
    },
    {
        path: 'manage-employments-published',
        name: RouteNames.AdministratorJobArchitectureManageEmploymentsPublished,
        component: async () => import('@/views/jobArchitecture/manageEmployments/employments/ManageEmploymentsPublishedV2.vue'),
        meta: {
            requiresAuth: true,
            userTypes: [Role.Administrator, Role.Root, Role.SystemAdministrator, Role.Consultant]
        },
    },
];
